/**
 * The frame of every exam screen (D-173): a fixed left panel (the section clock, the grid of the
 * questions, « Quitter l'examen ») and a card with the question and a bottom bar. Layout only: the
 * players keep their state and rules. The sections put their grid in the left panel by a portal,
 * so their state stays where it was; without a frame (a section tested alone) it renders in place.
 * On a phone the panel becomes a compact sticky clock and a collapsible grid.
 *
 * D-176: the page never scrolls during an exam on a computer: the frame fills the window, only the
 * panels scroll inside (examFrame.css), with a visible hint. The bar under the question stays on
 * screen everywhere, sticky on a phone or in a short window where the page has to scroll.
 */

import { Badge, Button, Dialog } from "@agencyos/ui-web";
import { ChevronDown, Flag, Headphones, Info, LogOut, Maximize2, Timer as Stopwatch, Volume2 } from "lucide-react";
import { type ReactNode, type Ref, createContext, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { readStorage, writeStorage } from "../lib/storage";
import { examAudioElement } from "./audio";
import { useAcademyPaths } from "./paths";
import { clock } from "./text";
import "./examFrame.css";

/** Longer than the autosave pauses of the answers and the writing drafts (700 ms, 1 500 ms): what
 * was just typed reaches the server before the sections close. */
export const QUIT_SETTLE_MS = 1800;

type QuitKind = "quit" | "abandon";
interface FrameValue {
  slot: HTMLElement | null;
  tools: HTMLElement | null;
  ask: (kind: QuitKind) => void;
}
const FrameContext = createContext<FrameValue | null>(null);

/** The whole section's seconds, for the bar of the clock: the time of the format when the player
 * knows it (mocks, practice), else the largest time seen on this device for this section. */
export function useClockTotal(key: string, remaining: number, formatSeconds: number): number {
  return useMemo(() => {
    const storageKey = `academy.clock.${key}`;
    const seen = Number(readStorage(storageKey)) || 0;
    const total = Math.max(seen, remaining, formatSeconds);
    if (total !== seen) writeStorage(storageKey, String(total));
    return total;
  }, [key, remaining, formatSeconds]);
}

/** The time a sitting's section has by its format; 0 when its time is not the format's own
 * (a discovery test is shorter, a placement has its own). */
export function formatSeconds(raw: unknown, kind: string): number {
  if (kind !== "mock" && kind !== "practice") return 0;
  const format = raw && typeof raw === "object" && "format" in raw ? (raw as { format: unknown }).format : null;
  if (!format || typeof format !== "object") return 0;
  const { minutes, transfer_minutes: transfer } = format as { minutes?: unknown; transfer_minutes?: unknown };
  return typeof minutes === "number" ? (minutes + (typeof transfer === "number" ? transfer : 0)) * 60 : 0;
}

/** What can scroll inside the frame: each says « there is more » while it has more below. */
const SCROLLERS = ".ac-document, .ac-question__sheet, .ac-listening__head, .ac-goethe-reading, .ac-goethe-split__read";

function useScrollHints(root: HTMLElement | null, label: string): void {
  useEffect(() => {
    if (!root) return;
    let frame = 0;
    const mark = () => {
      frame = 0;
      root.querySelectorAll<HTMLElement>(SCROLLERS).forEach((element) => {
        const scrolls = /auto|scroll/.test(getComputedStyle(element).overflowY) && element.scrollHeight - element.clientHeight > 4;
        // the keyboard can scroll it too, when nothing inside would take the focus
        if (scrolls && !element.hasAttribute("tabindex")) element.tabIndex = 0;
        if (scrolls && element.scrollHeight - element.clientHeight - element.scrollTop > 4) element.dataset.scrollMore = label;
        else delete element.dataset.scrollMore;
      });
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(mark);
    };
    schedule();
    const observer = new MutationObserver(schedule);
    observer.observe(root, { childList: true, subtree: true });
    root.addEventListener("scroll", schedule, { capture: true, passive: true });
    root.addEventListener("load", schedule, true); // the pictures take their height when loaded
    window.addEventListener("resize", schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      root.removeEventListener("scroll", schedule, { capture: true });
      root.removeEventListener("load", schedule, true);
      window.removeEventListener("resize", schedule);
    };
  }, [root, label]);
}

/** The focus goes to the question without moving the page; only where the page has to scroll (a
 * phone) and the question ended up out of sight is it brought back. A new question starts at the
 * top of its panel. */
export function focusQuestion(element: HTMLElement | null): void {
  if (!element) return;
  element.closest(".ac-question__sheet")?.scrollTo?.({ top: 0 });
  element.focus({ preventScroll: true });
  const page = document.scrollingElement;
  if (!page || page.scrollHeight <= window.innerHeight) return;
  const box = element.getBoundingClientRect();
  if (box.top < 72 || box.bottom > window.innerHeight - 96) element.scrollIntoView?.({ block: "center" });
}

/** The grid of a multi-question screen shows the chosen number, in its panel. */
export function revealItem(id: string): void {
  const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView?.({ block: "start", behavior: still ? "auto" : "smooth" }));
}

/** A / B / C / D choose, Enter confirms, on the one-question screens. Never while typing, in a
 * dialog, with a modifier, or (Enter) on a button or a link, which keep their own Enter. */
export function useAnswerKeys(count: number, onPick: (index: number) => void, onEnter: () => void): void {
  const pick = useRef(onPick);
  const enter = useRef(onEnter);
  pick.current = onPick;
  enter.current = onEnter;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest("textarea, select, [contenteditable='true'], input:not([type='radio']):not([type='range'])")) return;
      if (document.querySelector("[role='alertdialog'], [role='dialog']")) return;
      if (event.key === "Enter") {
        if (target?.closest("button, a, summary")) return;
        event.preventDefault();
        enter.current();
        return;
      }
      const index = "abcd".indexOf(event.key.toLowerCase());
      if (event.key.length === 1 && index >= 0 && index < count) {
        event.preventDefault();
        pick.current(index);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [count]);
}

function ExamClock({ seconds, total }: { seconds: number; total: number }) {
  const { t } = useTranslation();
  const tone = seconds <= 60 ? " ac-xclock--low" : seconds <= 300 ? " ac-xclock--soon" : "";
  const share = total > 0 ? Math.min(seconds / total, 1) : 1;
  // the clock is never read aloud every second: only its two last steps are announced
  const notice = seconds > 0 && seconds <= 60 ? t("examFrame.oneLeft") : seconds > 60 && seconds <= 300 ? t("examFrame.fiveLeft") : "";
  return (
    <div className={`ac-xclock${tone}`}>
      <Stopwatch aria-hidden size={22} className="ac-xclock__icon" />
      <p className="ac-xclock__time">
        <span role="timer" aria-label={t("examFrame.timeLeftIs", { time: clock(seconds) })} className="ac-xclock__digits ao-num">
          {clock(seconds)}
        </span>
        <span className="ac-xclock__label">{t("examFrame.timeLeft")}</span>
      </p>
      <div
        className="ac-xclock__bar"
        role="progressbar"
        aria-label={t("examFrame.timeBar")}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(share * 100)}
        aria-valuetext={clock(seconds)}
      >
        <span style={{ transform: `scaleX(${share})` }} />
      </div>
      <span className="visually-hidden" aria-live="polite">
        {notice}
      </span>
    </div>
  );
}

export interface ExamFrameProps {
  /** Modifiers of the page, e.g. « ac-player--co ». */
  className: string;
  icon?: ReactNode;
  eyebrow: string;
  title: string;
  headingRef?: Ref<HTMLHeadingElement>;
  training: boolean;
  seconds: number;
  clockTotal: number;
  /** Sections of the sitting and the current one: the thin steps under the title. */
  steps?: readonly string[];
  stepIndex?: number;
  /** What quitting now would not keep (a listening started, a recording in progress). */
  quitNote?: string;
  /** The saved note of the exam (D-176: in the quit confirmation and the left panel, no more under the question). */
  footnote?: ReactNode;
  children: ReactNode;
}

export function ExamFrame({ className, icon, eyebrow, title, headingRef, training, seconds, clockTotal, steps = [], stepIndex = 0, quitNote, footnote, children }: ExamFrameProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = useAcademyPaths();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const [tools, setTools] = useState<HTMLElement | null>(null);
  const [body, setBody] = useState<HTMLElement | null>(null);
  const [asked, setAsked] = useState<QuitKind | null>(null);
  const [leaving, setLeaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const value = useMemo<FrameValue>(() => ({ slot, tools, ask: setAsked }), [slot, tools]);
  useScrollHints(body, t("examFrame.scrollMore"));

  const leave = () => {
    setLeaving(true);
    timer.current = window.setTimeout(() => navigate(paths.home), QUIT_SETTLE_MS);
  };
  const fullScreen = () => void document.documentElement.requestFullscreen?.().catch(() => undefined);
  const canFullScreen = typeof document.documentElement.requestFullscreen === "function" && !document.fullscreenElement;
  const ask = () => setAsked("quit");

  return (
    <FrameContext.Provider value={value}>
      <div className={`ac-player ac-xf ${className}`}>
        <aside className="ac-xf__side ac-surface" aria-label={t("examFrame.panel")}>
          <div className="ac-xf__clock">
            <ExamClock seconds={seconds} total={clockTotal} />
            <button type="button" className="ac-xf__quit-icon" aria-label={t("examFrame.quit")} title={t("examFrame.quit")} onClick={ask}>
              <LogOut aria-hidden size={20} />
            </button>
          </div>
          <div ref={setSlot} className="ac-xf__slot" />
          <button type="button" className="ac-xf__quit" onClick={ask}>
            <LogOut aria-hidden size={18} />
            {t("examFrame.quit")}
          </button>
          {footnote && <p className="ac-xf__note">{footnote}</p>}
        </aside>

        <div className="ac-xf__main ac-surface">
          <header className="ac-xf__head">
            <div className="ac-player__bar-inner">
              <div className="ac-player__title">
                {icon}
                <div className="ac-player__title-text">
                  <p className="ac-player__eyebrow">{eyebrow}</p>
                  <h1 ref={headingRef} tabIndex={-1} className="ac-player__heading">
                    {title}
                  </h1>
                </div>
              </div>
              <div className="ac-player__tools">
                <div ref={setTools} className="ac-xf__tools" />
                <Badge tone={training ? "offer" : "default"}>{t(`academy.mode.${training ? "training" : "exam"}`)}</Badge>
                {canFullScreen && (
                  <button type="button" className="ac-icon-button ac-player__full" onClick={fullScreen}>
                    <Maximize2 aria-hidden size={17} />
                    <span className="visually-hidden">{t("academy.player.fullScreen")}</span>
                  </button>
                )}
              </div>
            </div>
            {steps.length > 1 && (
              <ol className="ac-player__steps" aria-hidden="true">
                {steps.map((skill, index) => (
                  <li key={skill} className={`ac-skill--${skill}${index < stepIndex ? " ac-player__step--done" : ""}${index === stepIndex ? " ac-player__step--on" : ""}`} />
                ))}
              </ol>
            )}
          </header>
          <div ref={setBody} className="ac-player__body">
            {children}
          </div>
        </div>
      </div>

      {createPortal(
      <Dialog
        open={asked !== null}
        role="alertdialog"
        title={t(asked === "abandon" ? "examFrame.abandonTitle" : "examFrame.quitTitle")}
        onClose={() => (leaving ? undefined : setAsked(null))}
        actions={
          <>
            <Button variant="ghost" disabled={leaving} onClick={() => setAsked(null)}>
              {t("examFrame.stay")}
            </Button>
            <Button variant="destructive" disabled={leaving} onClick={leave}>
              {leaving ? t("examFrame.saving") : t(asked === "abandon" ? "examFrame.abandonYes" : "examFrame.quitYes")}
            </Button>
          </>
        }
      >
        <p>{t(asked === "abandon" ? "examFrame.abandonBody" : "examFrame.quitBody")}</p>
        {quitNote && <p>{quitNote}</p>}
        {footnote && <p>{footnote}</p>}
      </Dialog>,
      document.body,
      )}
    </FrameContext.Provider>
  );
}

/** Puts its content in the left panel of the frame (in place when there is no frame). */
export function ExamSide({ children }: { children: ReactNode }) {
  const frame = useContext(FrameContext);
  if (!frame) return <>{children}</>;
  return frame.slot ? createPortal(children, frame.slot) : null;
}

/** Puts a small tool in the title row of the frame, where it takes no row (in place without a frame). */
export function ExamHeadTool({ children }: { children: ReactNode }) {
  const frame = useContext(FrameContext);
  if (!frame) return <>{children}</>;
  return frame.tools ? createPortal(children, frame.tools) : null;
}

export type CellState = "empty" | "done" | "past";

export interface ExamCell {
  key: string;
  text: string;
  state: CellState;
  current: boolean;
  /** Absent: the rules forbid going to it, the number is only shown. */
  onSelect?: () => void;
}

/** A grid of what cannot be visited (no going back): the questions gone by, those of the screen
 * and those to come. The server says how many were passed, never which were left empty. */
export function sequenceCells(total: number, shown: readonly { number: number; answered: boolean }[], fallbackFirst: number): ExamCell[] {
  const first = shown[0]?.number ?? fallbackFirst;
  const count = Math.max(total, first, shown[shown.length - 1]?.number ?? 0);
  const onScreen = new Map(shown.map((q) => [q.number, q.answered]));
  const nextUp = shown.find((q) => !q.answered)?.number;
  return Array.from({ length: count }, (_, i) => {
    const n = i + 1;
    const here = shown.length ? onScreen.has(n) : n === first;
    const state: CellState = onScreen.get(n) ? "done" : n < first ? "past" : "empty";
    return { key: String(n), text: String(n), state, current: here && (n === nextUp || (nextUp === undefined && n === first)) };
  });
}

/** « Navigation des questions »: the numbered squares, their legend, and what ends the section. */
export function ExamNav({ cells, children }: { cells: readonly ExamCell[]; children?: ReactNode }) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const answered = cells.filter((c) => c.state === "done").length;
  const hasPast = cells.some((c) => c.state === "past");
  const label = (cell: ExamCell) =>
    t(cell.state === "done" ? "academy.navigator.cellAnswered" : cell.state === "past" ? "examFrame.cellPast" : "academy.navigator.cellEmpty", { n: cell.text });
  return (
    <ExamSide>
      <section className="ac-xnav" aria-labelledby={`${id}-title`}>
        <div className="ac-xnav__head">
          <h2 id={`${id}-title`} className="ac-xnav__title">
            {t("examFrame.navTitle")}
          </h2>
          <p className="ac-caption ao-num" role="status">
            {hasPast ? t("examFrame.passed", { n: cells.filter((c) => c.state !== "empty").length, total: cells.length }) : t("academy.navigator.answered", { answered, total: cells.length })}
          </p>
          <button
            type="button"
            className="ac-xnav__toggle"
            aria-expanded={open}
            aria-controls={`${id}-body`}
            aria-label={t(open ? "examFrame.hideGrid" : "examFrame.showGrid")}
            onClick={() => setOpen(!open)}
          >
            <ChevronDown aria-hidden size={20} />
          </button>
        </div>
        <div id={`${id}-body`} className={`ac-xnav__body${open ? " ac-xnav__body--open" : ""}`}>
          <ol className="ac-qgrid ac-xnav__grid" aria-label={t("academy.navigator.gridLabel")}>
            {cells.map((cell) => {
              const look = `ac-qcell${cell.state === "done" ? " ac-qcell--done" : ""}${cell.state === "past" ? " ac-qcell--past" : ""}${cell.current ? " ac-qcell--on" : ""}${cell.text.length > 2 ? " ac-qcell--wide" : ""}`;
              return (
                <li key={cell.key} className={cell.text.length > 2 ? "ac-xnav__wide" : undefined}>
                  {cell.onSelect ? (
                    <button
                      type="button"
                      className={look}
                      aria-current={cell.current ? "step" : undefined}
                      aria-label={label(cell)}
                      title={label(cell)}
                      onClick={() => {
                        cell.onSelect?.();
                        setOpen(false);
                      }}
                    >
                      {cell.text}
                    </button>
                  ) : (
                    <span className={`${look} ac-qcell--static`} aria-current={cell.current ? "step" : undefined}>
                      <span aria-hidden="true">{cell.text}</span>
                      <span className="visually-hidden">{label(cell)}</span>
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
          <p className="ac-legend-row" aria-hidden="true">
            <span>
              <span className="ac-qcell ac-qcell--on ac-qcell--mini" /> {t("examFrame.legendCurrent")}
            </span>
            {hasPast && (
              <span>
                <span className="ac-qcell ac-qcell--past ac-qcell--mini" /> {t("examFrame.legendPast")}
              </span>
            )}
            <span>
              <span className="ac-qcell ac-qcell--done ac-qcell--mini" /> {t("examFrame.legendDone")}
            </span>
            <span>
              <span className="ac-qcell ac-qcell--mini" /> {t("examFrame.legendEmpty")}
            </span>
          </p>
          {children}
        </div>
      </section>
    </ExamSide>
  );
}

/** The bar under the question: left, a row of pills in the centre, right, and a tiny hint. */
export function ExamBar({ left, pills, right, hint }: { left?: ReactNode; pills?: ReactNode; right?: ReactNode; hint?: ReactNode }) {
  return (
    <div className="ac-xbar">
      <div className="ac-xbar__left">{left}</div>
      <div className="ac-xbar__pills">{pills}</div>
      <div className="ac-xbar__right">{right}</div>
      {hint && <p className="ac-xbar__hint">{hint}</p>}
    </div>
  );
}

export function ExamPill({ children }: { children: ReactNode }) {
  return <span className="ac-xpill ao-num">{children}</span>;
}

/** The red pill of a per-question time. */
export function ExamCountdown({ seconds, label, tone = "answer" }: { seconds: number; label: string; tone?: "answer" | "reading" }) {
  return (
    <span className={`ac-xcount ac-xcount--${tone} ao-num`} role="timer" aria-label={`${label} ${clock(seconds)}`}>
      {clock(seconds)}
    </span>
  );
}

/** « Abandonner »: where going back is forbidden. No sitting can be given up on the server, so it
 * leaves like « Quitter » (the answers stay, the sitting can be resumed); nothing without a frame. */
export function ExamAbandon() {
  const { t } = useTranslation();
  const frame = useContext(FrameContext);
  if (!frame) return null;
  return (
    <button type="button" className="ac-xf__abandon" onClick={() => frame.ask("abandon")}>
      <Flag aria-hidden size={16} />
      <span className="ac-xf__abandon-label">{t("examFrame.abandon")}</span>
    </button>
  );
}

/** The confirmation before a section with free navigation is closed for good. */
export function FinishDialog({ open, unanswered, busy, onCancel, onConfirm }: { open: boolean; unanswered: number; busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  const { t } = useTranslation();
  return (
    createPortal(
    <Dialog
      open={open}
      role="alertdialog"
      title={t("academy.navigator.finish")}
      onClose={onCancel}
      actions={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? t("common.loading") : t("academy.navigator.finishYes")}
          </Button>
        </>
      }
    >
      <p>{unanswered > 0 ? t("academy.navigator.confirmMissing", { count: unanswered }) : t("academy.navigator.confirmAll")}</p>
    </Dialog>,
    document.body,
    )
  );
}

/** Reads the time of the shared exam audio while it plays: nothing here can pause or seek it. */
function useAudioClock(active: boolean): { time: number; duration: number } | null {
  const [clockValue, setClockValue] = useState<{ time: number; duration: number } | null>(null);
  useEffect(() => {
    if (!active) {
      setClockValue(null);
      return;
    }
    const read = () => {
      const element = examAudioElement();
      const { currentTime, duration } = element;
      setClockValue(typeof currentTime === "number" && typeof duration === "number" && Number.isFinite(duration) && duration > 0 ? { time: currentTime, duration } : null);
    };
    read();
    const timer = window.setInterval(read, 500);
    return () => window.clearInterval(timer);
  }, [active]);
  return clockValue;
}

/** « Extrait audio »: the listening of a player in a panel. While the generated audio plays: a
 * progress bar that shows the time without moving it, and the volume (the single listening stays
 * whole: neither pause nor seek). The device's voices have no time to show. */
export function ExamAudioPanel({ playing = false, note, children }: { playing?: boolean; note?: ReactNode; children: ReactNode }) {
  const { t } = useTranslation();
  const time = useAudioClock(playing);
  const [open, setOpen] = useState(false);
  const noteId = useId();
  return (
    <section className="ac-xaudio" aria-label={t("examFrame.audioTitle")}>
      <p className="ac-xaudio__title">
        <Headphones aria-hidden size={16} />
        {t("examFrame.audioTitle")}
      </p>
      {children}
      {time && (
        <div className="ac-xaudio__track">
          <span className="ao-num">{clock(Math.floor(time.time))}</span>
          <div
            className="ac-xaudio__bar"
            role="progressbar"
            aria-label={t("examFrame.audioProgress")}
            aria-valuemin={0}
            aria-valuemax={Math.ceil(time.duration)}
            aria-valuenow={Math.floor(time.time)}
          >
            <span style={{ transform: `scaleX(${Math.min(time.time / time.duration, 1)})` }} />
          </div>
          <span className="ao-num">{clock(Math.ceil(time.duration))}</span>
          <label className="ac-xaudio__volume">
            <Volume2 aria-hidden size={16} />
            <span className="visually-hidden">{t("examFrame.volume")}</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              defaultValue={examAudioElement().volume}
              onChange={(event) => {
                examAudioElement().volume = Number(event.target.value);
              }}
            />
          </label>
        </div>
      )}
      {note && (
        <>
          <button type="button" className="ac-xaudio__info" aria-expanded={open} aria-controls={noteId} aria-label={t("examFrame.audioInfo")} title={t("examFrame.audioInfo")} onClick={() => setOpen(!open)}>
            <Info aria-hidden size={18} />
          </button>
          <div id={noteId} className="ac-xaudio__note" hidden={!open}>
            {note}
          </div>
        </>
      )}
    </section>
  );
}
